import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';
import { 
  AlertTriangle, 
  MessageCircle, 
  CreditCard, 
  UserMinus,
  Gift,
  Clock,
  ChevronRight,
  Shield
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';

interface AlertItem {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  title: string;
  description: string;
  count?: number;
  link?: string;
  linkLabel?: string;
}

interface OwnerAlertsProps {
  clients: Array<{
    id: string;
    name: string;
    email: string;
    plan: string;
    paid: boolean;
    status: string;
    subscription_end: string;
    has_access?: boolean | null;
  }>;
}

const OwnerAlerts = ({ clients }: OwnerAlertsProps) => {
  const [openTickets, setOpenTickets] = useState(0);
  const [pendingAffiliatePayouts, setPendingAffiliatePayouts] = useState(0);
  const [pendingPayoutAmount, setPendingPayoutAmount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAlertData();
  }, []);

  const loadAlertData = async () => {
    try {
      const [ticketsRes, affiliatesRes] = await Promise.all([
        supabase
          .from('support_tickets')
          .select('id', { count: 'exact' })
          .eq('status', 'open'),
        supabase
          .from('affiliate_rewards')
          .select('id, amount')
          .eq('status', 'pending'),
      ]);

      setOpenTickets(ticketsRes.count || 0);
      const affiliates = affiliatesRes.data || [];
      setPendingAffiliatePayouts(affiliates.length);
      setPendingPayoutAmount(affiliates.reduce((sum, a) => sum + a.amount, 0));
    } catch (err) {
      console.error('Error loading alerts:', err);
    } finally {
      setLoading(false);
    }
  };

  const now = new Date();
  
  // Pending payments (not paid, not freemium)
  const pendingPayments = clients.filter(c => !c.paid && c.plan.toLowerCase() !== 'freemium');
  
  // Expiring soon (within 7 days)
  const expiringSoon = clients.filter(c => {
    const daysLeft = Math.ceil((new Date(c.subscription_end).getTime() - now.getTime()) / 86400000);
    return daysLeft > 0 && daysLeft <= 7 && c.paid;
  });

  // Expired but still have access (data inconsistency)
  const accessIssues = clients.filter(c => {
    const expired = new Date(c.subscription_end) < now;
    return expired && c.has_access === true;
  });

  // Expiring in 30 days
  const expiringMonth = clients.filter(c => {
    const daysLeft = Math.ceil((new Date(c.subscription_end).getTime() - now.getTime()) / 86400000);
    return daysLeft > 7 && daysLeft <= 30 && c.paid;
  });

  const alerts: AlertItem[] = [];

  if (accessIssues.length > 0) {
    alerts.push({
      id: 'access-issues',
      severity: 'critical',
      title: 'Access Inconsistencies',
      description: `${accessIssues.length} expired client(s) still have access`,
      count: accessIssues.length,
      link: '/admin/crm',
      linkLabel: 'Review in CRM',
    });
  }

  if (openTickets > 0) {
    alerts.push({
      id: 'open-tickets',
      severity: openTickets > 3 ? 'critical' : 'warning',
      title: 'Open Support Tickets',
      description: `${openTickets} ticket(s) awaiting response`,
      count: openTickets,
      link: '/admin/support',
      linkLabel: 'View Tickets',
    });
  }

  if (pendingPayments.length > 0) {
    alerts.push({
      id: 'pending-payments',
      severity: 'warning',
      title: 'Pending Payments',
      description: `${pendingPayments.length} client(s) haven't paid yet`,
      count: pendingPayments.length,
      link: '/admin/crm',
      linkLabel: 'View in CRM',
    });
  }

  if (expiringSoon.length > 0) {
    alerts.push({
      id: 'expiring-soon',
      severity: 'warning',
      title: 'Expiring This Week',
      description: `${expiringSoon.length} member(s) expiring within 7 days`,
      count: expiringSoon.length,
      link: '/admin/crm',
      linkLabel: 'View Members',
    });
  }

  if (pendingAffiliatePayouts > 0) {
    alerts.push({
      id: 'affiliate-payouts',
      severity: 'info',
      title: 'Affiliate Payouts Due',
      description: `$${pendingPayoutAmount.toFixed(2)} pending across ${pendingAffiliatePayouts} reward(s)`,
      count: pendingAffiliatePayouts,
      link: '/admin/affiliates',
      linkLabel: 'Process Payouts',
    });
  }

  if (expiringMonth.length > 0) {
    alerts.push({
      id: 'expiring-month',
      severity: 'info',
      title: 'Expiring This Month',
      description: `${expiringMonth.length} member(s) expiring within 30 days`,
      count: expiringMonth.length,
    });
  }

  if (alerts.length === 0) {
    alerts.push({
      id: 'all-good',
      severity: 'info',
      title: 'All Clear',
      description: 'No urgent items require your attention',
    });
  }

  const getSeverityStyles = (severity: AlertItem['severity']) => {
    switch (severity) {
      case 'critical': return { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600', badge: 'bg-red-100 text-red-700' };
      case 'warning': return { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600', badge: 'bg-amber-100 text-amber-700' };
      case 'info': return { bg: 'bg-blue-50', border: 'border-blue-200', icon: 'text-blue-600', badge: 'bg-blue-100 text-blue-700' };
    }
  };

  const getIcon = (severity: AlertItem['severity']) => {
    switch (severity) {
      case 'critical': return <Shield className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      case 'info': return <Clock className="w-4 h-4" />;
    }
  };

  const criticalCount = alerts.filter(a => a.severity === 'critical').length;
  const warningCount = alerts.filter(a => a.severity === 'warning').length;

  return (
    <div className="p-5 sm:p-6 bg-white border border-border rounded-2xl shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Action Items</h3>
        </div>
        <div className="flex gap-1.5">
          {criticalCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-bold">
              {criticalCount} critical
            </span>
          )}
          {warningCount > 0 && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold">
              {warningCount} warning
            </span>
          )}
        </div>
      </div>
      <ScrollArea className="h-[420px]">
        <div className="space-y-2">
          {alerts.map(alert => {
            const styles = getSeverityStyles(alert.severity);
            return (
              <div key={alert.id} className={`p-3 ${styles.bg} border ${styles.border} rounded-xl`}>
                <div className="flex items-start gap-2.5">
                  <div className={`mt-0.5 ${styles.icon}`}>
                    {getIcon(alert.severity)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-semibold text-foreground">{alert.title}</p>
                      {alert.count !== undefined && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${styles.badge}`}>
                          {alert.count}
                        </span>
                      )}
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{alert.description}</p>
                    {alert.link && (
                      <Link 
                        to={alert.link} 
                        className="inline-flex items-center gap-0.5 text-[10px] font-medium text-primary mt-1.5 hover:underline"
                      >
                        {alert.linkLabel} <ChevronRight className="w-3 h-3" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </ScrollArea>
    </div>
  );
};

export default OwnerAlerts;
